import { useCallback } from 'react';
import { createUuid } from '~/_shared/utils/createUuid';
import type { ModalProps } from '~/modal/modalType.enum';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  type ChartId, type ChartMetric, isChartBoundaryStatistics,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import { useMapSettingsCharts } from '~/store/mapSettings/data/charts/useMapSettingsCharts';
import { useDefaultBTGStats } from '~/store/stats/hooks/useDefaultBTGStats';
import { useManageStats } from '~/store/stats/hooks/useManageStats';
import { createEmptyBoundaryStatItem } from '~/store/stats/stats.factory';
import { findBoundaryGroupStatItem } from '~/store/stats/stats.helpers';
import { useBoundaryGroupStatsSelector } from '~/store/stats/stats.selectors';
import type { StatItem } from '~/store/stats/stats.state';
import { chartEditorMetricToChartMetric } from './chartEditor.helpers';
import {
  ChartEditorModalContainer, type ChartEditorModalContainerProps,
} from './chartEditorModal.container';

export const ChartEditorContainer = ({
  chartId,
  defaultStatsBoundaryGroupId,
  ...modalProps
}: ModalProps<{
  chartId?: ChartId;
  defaultStatsBoundaryGroupId?: BoundaryGroupId;
}>) => {
  const boundaryGroupsStats = useBoundaryGroupStatsSelector();
  const { charts, addOrUpdateChart } = useMapSettingsCharts();
  const { calculateStats } = useManageStats();
  const { getBoundaryDefaultStatsChartForBoundaryGroup } = useDefaultBTGStats();

  const savedChart = charts.find(c => c.id === chartId);
  const defaultStatsChartForBTG = defaultStatsBoundaryGroupId
    ? getBoundaryDefaultStatsChartForBoundaryGroup(defaultStatsBoundaryGroupId)
    : undefined;

  const chart = savedChart || defaultStatsChartForBTG;

  const onSave: ChartEditorModalContainerProps['onSave'] = useCallback(({
    name,
    metrics,
  }) => {
    if (!chart) {
      return;
    }

    const allChartMetrics: ChartMetric[] = [];
    const newStatItems: StatItem[] = [];

    if (isChartBoundaryStatistics(chart)) {
      metrics.forEach((metricData) => {
        const chartMetric = chartEditorMetricToChartMetric(metricData);

        if (!chartMetric) {
          return;
        }

        const statItem = createEmptyBoundaryStatItem({
          boundaryGroupId: chart.boundaryGroupId,
          chartMetric,
        });

        const existingStatItem = findBoundaryGroupStatItem(boundaryGroupsStats, statItem);
        if (existingStatItem) {
          allChartMetrics.push(existingStatItem);
        }
        else {
          newStatItems.push(statItem);
          allChartMetrics.push(statItem);
        }
      });
    }

    calculateStats(newStatItems);

    addOrUpdateChart({
      ...chart,
      // create new chart when using a default chart to avoid conflicts with default charts
      id: chartId || createUuid(),
      title: name,
      metrics: allChartMetrics,
    });

  }, [chart, chartId, calculateStats, addOrUpdateChart, boundaryGroupsStats]);

  if (!chart) {
    return null;
  }

  return (
    <ChartEditorModalContainer
      {...modalProps}
      showBoundaryGroupSelector={false}
      defaultBoundaryGroupId={isChartBoundaryStatistics(chart) ? chart.boundaryGroupId : undefined}
      defaultName={chart.title}
      defaultMetrics={chart.metrics}
      onSave={onSave}
    />
  );
};
